import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useEnqueueSnackbar } from 'hooks/useEnqueueSnackbar';
import type { UseModalResult } from 'hooks/useModal';
import { ConfirmDialog } from 'components/common';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import {
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';
import { useDeleteGoToTest } from '../api';

interface CommonProps {
  id: number;
  name: string;
}

interface DeleteItemProps
  extends CommonProps,
    Pick<UseModalResult, 'open' | 'close' | 'isOpen'> {
}

const DeleteGoToTest: FC<DeleteItemProps> = ({ isOpen, close, id, name }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();

  const deleteGoToTest = useDeleteGoToTest({
    config: createCabinetMutationTracking({
      platform: 'facebook',
      entity: 'go_to_test',
      mode: 'delete',
      onSuccess: () => {
        onShowAlert(`"${name}" deleted`);
        close();
        navigate(`/cabinet/facebook/go-to-test`, { replace: true });
      },
      onError: () => {
        setErrorMessage('Internal Error');
      },
    }),
  });

  const onConfirm = useCallback(() => {
    setErrorMessage(null);
    trackCabinetSubmitAttempt('facebook', 'go_to_test', 'delete', { id });
    deleteGoToTest.mutate({ id });
  }, [id, deleteGoToTest]);

  return (
    <>
      <ConfirmDialog
        title={`Delete ${name}?`}
        text={
          <>
            <Typography variant="body1">
              {name} will be deleted without the possibility of recovery
            </Typography>
            {!!errorMessage && (
              <Typography
                variant="body1"
                sx={(t) => ({ color: t.palette.error.main, pt: 2 })}
              >
                {errorMessage}
              </Typography>
            )}
          </>
        }
        confirmButton="Delete Go to Test"
        cancelButton="Cancel"
        isOpen={isOpen}
        isPending={deleteGoToTest.isPending}
        close={() => {
          !deleteGoToTest.isPending ? close() : null;
        }}
        onCancel={() => {
          !deleteGoToTest.isPending ? close() : null;
        }}
        onConfirm={onConfirm}
      />
    </>
  );
};

export default DeleteGoToTest;
